import { memo, useMemo } from 'react'

import { cn } from '@/lib/utils'
import {
  MAP_VIEWBOX,
  MAP_OUTLINE_PATH,
  activeLinePlacement,
} from '../mapPlacement'
import type { PhoneLine } from '../types'

type HoverHandler = (lineNumber: number, rect: DOMRect) => void

const MapDot = memo(function MapDot({
  lineNumber,
  x,
  y,
  isHovered,
  onHover,
  onLeave,
}: {
  lineNumber: number
  x: number
  y: number
  isHovered: boolean
  onHover: HoverHandler
  onLeave: () => void
}) {
  return (
    <g>
      {/* ripple */}
      <circle
        cx={x}
        cy={y}
        r={2.2}
        className="origin-center animate-ping fill-fuchsia-500 [transform-box:fill-box] dark:fill-fuchsia-400"
        opacity={0.55}
      />
      {/* core */}
      <circle
        cx={x}
        cy={y}
        r={1.6}
        className={cn(
          'fill-fuchsia-500 dark:fill-fuchsia-400',
          isHovered && 'stroke-blue-500 [stroke-width:1.5]'
        )}
      />
      {/* larger transparent hit target (hover only — not clickable) */}
      <circle
        cx={x}
        cy={y}
        r={4.5}
        fill="transparent"
        onMouseEnter={(e) =>
          onHover(lineNumber, e.currentTarget.getBoundingClientRect())
        }
        onMouseLeave={onLeave}
      />
    </g>
  )
})

/**
 * California map with one pulsing dot per active call. Dot positions are FAKED,
 * derived deterministically from each call's stable `phone_line_id` (see
 * constants), so a dot keeps its spot across polls until that call ends.
 * memo() skips the whole subtree on polls where line data is unchanged; dots
 * are memoized so a hover only re-renders the two affected dots.
 */
export const PhoneLineMap = memo(function PhoneLineMap({
  lines,
  hoveredLine,
  className,
  onHover,
  onLeave,
}: {
  lines: PhoneLine[]
  hoveredLine: number | null
  /** Sizing classes for the <svg>; defaults to width-driven (`h-auto w-full`).
   *  Pass e.g. `h-full w-auto` to make it height-driven and scale to fit a box. */
  className?: string
  onHover: HoverHandler
  onLeave: () => void
}) {
  // TODO(phone-lines): dot locations are faked — placement is a deterministic
  // hash of phone_line_id, not real geography. A future PR will derive each
  // dot's location from the dialed phone number's area code, and then scale the
  // map to the smallest region that fits all active lines (Country -> State ->
  // City) instead of the fixed California outline.
  const dots = useMemo(() => {
    const out: {
      lineNumber: number
      phoneLineId: string
      x: number
      y: number
    }[] = []
    for (const line of lines) {
      if (line.status !== 'active') continue
      // The contract types phone_line_id as a string, but every other field on an
      // active line is a nullable pass-through, and this one seeds the placement
      // hash — a null would throw inside activeLinePlacement (it reads .length) and
      // blow up the whole page from one bad row. Drop the dot instead.
      if (!line.phone_line_id) continue
      const placement = activeLinePlacement(line.phone_line_id)
      out.push({
        lineNumber: line.line_number,
        phoneLineId: line.phone_line_id,
        x: placement.x,
        y: placement.y,
      })
    }
    return out
  }, [lines])

  return (
    <svg
      viewBox={MAP_VIEWBOX}
      role="img"
      aria-label="California map of active calls"
      className={cn('block', className ?? 'h-auto w-full')}>
      <path
        d={MAP_OUTLINE_PATH}
        className="fill-slate-100 stroke-slate-300 dark:fill-slate-800 dark:stroke-slate-600"
        strokeWidth={1.25}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {dots.map((dot) => (
        <MapDot
          key={dot.phoneLineId}
          lineNumber={dot.lineNumber}
          x={dot.x}
          y={dot.y}
          isHovered={hoveredLine === dot.lineNumber}
          onHover={onHover}
          onLeave={onLeave}
        />
      ))}
    </svg>
  )
})
